import React, { useState } from 'react';
import { usePopper } from 'react-popper';
import useSelectedChain from 'hooks/useSelectedChain';
import { useAccount, useConfig } from 'wagmi';
import {
  CustomChain,
  chains as defaultChains,
} from './WalletProvider/WalletProvider';
import { chainLogoSrc } from 'constants/constants';

type Props = {
  isOnBottom?: boolean;
};

const NetworkSelectorContainer: React.FC<Props> = ({ isOnBottom }) => {
  const { chainId, setChainId } = useSelectedChain();
  const { chain, isConnected } = useAccount();
  const { chains } = useConfig();
  const allNetworks = (
    chains.length > 0 ? chains : defaultChains
  );
  const networks = allNetworks.filter((c) => c.id !== chainId);
  const network =
    (chain as CustomChain | undefined) ||
    allNetworks.find((c) => c.id === chainId);

  const [isActive, setIsActive] = useState(false);
  const [referenceElement, setReferenceElement] = React.useState<unknown>(null);
  const [popperElement, setPopperElement] = React.useState<unknown>(null);
  const {
    styles,
    attributes,
    update: popperUpdate,
  } = usePopper(referenceElement as Element, popperElement as HTMLElement, {
    placement: isOnBottom ? 'top-end' : 'bottom-end',
    modifiers: [
      {
        name: 'offset',
        options: {
          offset: isOnBottom ? [0, 100] : [0, 8],
        },
      },
    ],
  });

  React.useEffect(() => {
    // close popper when clicked out
    const handleOnClick = (ev) => {
      if (
        !(ev.composedPath() as Array<HTMLElement>).includes(
          popperElement as HTMLElement
        ) &&
        !(ev.composedPath() as Array<HTMLElement>).includes(
          referenceElement as HTMLElement
        )
      ) {
        setIsActive(false);
      }
    };
    document.addEventListener('click', handleOnClick);
    return () => document.removeEventListener('click', handleOnClick);
  });

  const setPopperIsActive = (
    event: React.MouseEvent<HTMLElement, MouseEvent>,
    val: boolean
  ) => {
    event.preventDefault();
    if (popperUpdate) {
      void popperUpdate();
    }
    setIsActive(val);
  };

  const handleOnSelectChain = (
    event: React.MouseEvent<HTMLDivElement, MouseEvent>,
    newNetwork: CustomChain | undefined
  ) => {
    event.preventDefault();
    setPopperIsActive(event, false);
    setChainId(newNetwork?.id ? newNetwork.id : 1);
  };

  const isNetworkUnsupported = (isConnected && !chain);

  return (
    <>
      <button
        type="button"
        onClick={(event) => setPopperIsActive(event, !isActive)}
        ref={setReferenceElement}
        className="nav-button-bg-color primary-text-color ml-3 rounded-[10px] cursor-pointer
          border-none
          min-w-0 sm:min-w-[154px]
          h-10"
      >
        <div className="flex flex-row items-center justify-between px-3">
          <div className="flex flex-row items-center">
            {isNetworkUnsupported ? (
              <>Select Network</>
            ) : (
              <>
                <img src={chainLogoSrc(network?.id)} alt={network?.name} className="h-5 mr-2" />
                <div className="font-medium hidden sm:inline-block">
                  {network?.name}
                </div>
              </>
            )}
          </div>
          <img
            src="../../assets/image/chevron-down.svg"
            alt="expand"
            className="w-3 ml-2"
          />
        </div>
      </button>
      <div
        className={`ichi-balance-bg-color z-[5000] rounded-[10px] absolute right-full shadow-[0_2px_50px_0_rgba(0,0,0,0.25)] ${
          isActive ? 'inline' : 'hidden'
        }`}
        ref={setPopperElement}
        style={styles.popper}
        {...attributes.popper}
        data-dd-action-name="Toggle Network Popper"
      >
        <div className="px-[20px]">
          {networks.map((netwk) => (
            <div
              key={netwk.name}
              className="flex flex-row items-center h-11 cursor-pointer primary-text-color"
              onClick={(event) => handleOnSelectChain(event, netwk)}
            >
              <div className="flex flex-row items-center justify-center w-[30px] mr-3">
                <img 
                  src={chainLogoSrc(netwk.id)} 
                  alt={netwk.name} 
                  className="h-5" />
              </div>
              {netwk.name}
            </div>
          ))}
        </div>
      </div>
    </>
  );
};

export default NetworkSelectorContainer;
